import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Simple hash for offline credential caching (not crypto-grade, but sufficient for local fallback)
const hashCredential = async (value) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(value + "_bbh_salt_2026");
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const OFFLINE_CREDENTIALS_KEY = "offline_credentials";
const OFFLINE_USER_KEY = "offline_user";

// Cache credentials after a successful online login
const cacheCredentials = async (type, credential, userData) => {
  try {
    const hash = await hashCredential(credential);
    const existing = JSON.parse(localStorage.getItem(OFFLINE_CREDENTIALS_KEY) || "{}");
    existing[type + ":" + hash] = true;
    localStorage.setItem(OFFLINE_CREDENTIALS_KEY, JSON.stringify(existing));
    localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(userData));
  } catch (e) {
    console.warn("Failed to cache offline credentials:", e);
  }
};

// Verify credentials against cached hashes
const verifyOfflineCredential = async (type, credential) => {
  try {
    const hash = await hashCredential(credential);
    const existing = JSON.parse(localStorage.getItem(OFFLINE_CREDENTIALS_KEY) || "{}");
    if (existing[type + ":" + hash]) {
      const userData = JSON.parse(localStorage.getItem(OFFLINE_USER_KEY));
      return userData || null;
    }
  } catch (e) {
    console.warn("Offline credential check failed:", e);
  }
  return null;
};

const isNetworkError = (error) => {
  return !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error" || !navigator.onLine);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const interceptorRef = useRef(null);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Set up axios interceptor for auth header + global 401 handling
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }

    // Remove previous interceptor before adding a new one
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }

    // Global 401 interceptor — clears stale token and sends user to login
    // BUT only on actual 401 responses, NOT on network errors
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !isNetworkError(error)) {
          localStorage.removeItem("auth_token");
          axios.defaults.headers.common["Authorization"] = "";
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
  }, [token]);

  // Check if setup is needed and verify token on mount
  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      // First check if setup is required
      const setupRes = await axios.get(`${API}/api/auth/check-setup`);
      if (setupRes.data.setup_required) {
        setSetupRequired(true);
        setLoading(false);
        return;
      }
      setSetupRequired(false);

      // If we have a token, verify it
      if (token) {
        const res = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data.user);
        // Cache the verified user for offline use
        localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(res.data.user));
      }
    } catch (error) {
      // If it's a network error, try to restore cached session
      if (isNetworkError(error)) {
        console.log("Network unavailable — attempting offline session restore");
        const cachedUser = localStorage.getItem(OFFLINE_USER_KEY);
        if (cachedUser && token) {
          try {
            setUser(JSON.parse(cachedUser));
            console.log("Offline session restored from cache");
            setLoading(false);
            return;
          } catch (e) {
            console.warn("Failed to parse cached user");
          }
        }
      }
      // Actual auth failure (401, etc.) — clear session
      if (!isNetworkError(error)) {
        localStorage.removeItem("auth_token");
        setToken(null);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = async (username, password) => {
    try {
      const res = await axios.post(`${API}/api/auth/login`, { username, password });
      const { token: newToken, user: userData } = res.data;

      localStorage.setItem("auth_token", newToken);
      // Set the auth header synchronously BEFORE flipping React state, so any
      // child `useEffect` that fires on the next render (e.g. UserManagement
      // calling /api/auth/users) sees the new token. Otherwise the parent's
      // token useEffect can race the child fetch and the call goes un-authed.
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);

      // Cache for offline use
      await cacheCredentials("password", username + ":" + password, userData);

      return userData;
    } catch (error) {
      // If network error, try offline login
      if (isNetworkError(error)) {
        const cachedUser = await verifyOfflineCredential("password", username + ":" + password);
        if (cachedUser) {
          setUser(cachedUser);
          // Set a placeholder token so isAuthenticated is true
          const offlineToken = "offline_" + Date.now();
          localStorage.setItem("auth_token", offlineToken);
          setToken(offlineToken);
          return cachedUser;
        }
        throw new Error("No internet connection and no cached credentials for this user");
      }
      throw error;
    }
  };

  const loginWithPin = async (pin) => {
    try {
      const res = await axios.post(`${API}/api/auth/pin-login`, { pin });
      const { token: newToken, user: userData } = res.data;

      localStorage.setItem("auth_token", newToken);
      // See loginWithPassword for why we set the axios header synchronously.
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);

      // Cache for offline use
      await cacheCredentials("pin", pin, userData);

      return userData;
    } catch (error) {
      // If network error, try offline login
      if (isNetworkError(error)) {
        const cachedUser = await verifyOfflineCredential("pin", pin);
        if (cachedUser) {
          setUser(cachedUser);
          const offlineToken = "offline_" + Date.now();
          localStorage.setItem("auth_token", offlineToken);
          setToken(offlineToken);
          return cachedUser;
        }
        throw new Error("No internet connection and no cached credentials for this PIN");
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }

    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
    // Note: We do NOT clear offline_credentials or offline_user on logout
    // so they can still log back in offline
  };

  const setupAdmin = async (username, displayName, password) => {
    const res = await axios.post(`${API}/api/auth/setup`, {
      username,
      display_name: displayName,
      role: "admin",
      password
    });

    setSetupRequired(false);
    return res.data;
  };

  const isAdmin = user?.role === "admin";
  const isCashier = user?.role === "cashier";
  const isAuthenticated = !!user;

  // Define which routes each role can access
  const allowedRoutes = {
    admin: "all", // Admin can access everything
    cashier: [
      "/",
      "/sales",
      "/cashup",
      "/online-orders",
      "/print-stocktake",
      "/staff-clock",
      "/bin-assign",
      "/stock-count",
      "/stock-count/new",
    ]
  };

  const canAccess = (path) => {
    if (!user) return false;
    if (user.role === "admin") return true;

    const routes = allowedRoutes[user.role] || [];
    return routes.includes(path);
  };

  const value = {
    user,
    token,
    loading,
    setupRequired,
    isAuthenticated,
    isAdmin,
    isCashier,
    isOffline,
    loginWithPassword,
    loginWithPin,
    logout,
    setupAdmin,
    canAccess,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
